import { useState } from "react";
import { Link } from "react-router-dom";
import type { VenueDetail } from "../../../mocks/allVenues";
import MapVenueCard from "./MapVenueCard";

const CATEGORY_TABS = [
  { key: "all", label: "Tümü", icon: "ri-apps-line" },
  { key: "historical", label: "Tarihi", icon: "ri-ancient-pavilion-line" },
  { key: "food", label: "Lezzet", icon: "ri-restaurant-line" },
  { key: "entertainment", label: "Eğlence", icon: "ri-gamepad-line" },
  { key: "accommodation", label: "Konaklama", icon: "ri-hotel-line" },
  { key: "nature", label: "Doğa", icon: "ri-plant-line" },
  { key: "sightseeing", label: "Gezi", icon: "ri-eye-line" },
];

interface MapSidebarProps {
  venues: VenueDetail[];
  selectedVenueId: string | null;
  onSelectVenue: (venue: VenueDetail) => void;
}

export default function MapSidebar({ venues, selectedVenueId, onSelectVenue }: MapSidebarProps) {
  const [activeCategory, setActiveCategory] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOpenOnly, setShowOpenOnly] = useState(false);

  const filtered = venues.filter((v) => {
    const matchCat = activeCategory === "all" || v.categorySlug === activeCategory;
    const matchSearch =
      searchQuery === "" ||
      v.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.district.toLowerCase().includes(searchQuery.toLowerCase()) ||
      v.category.toLowerCase().includes(searchQuery.toLowerCase());
    const matchOpen = !showOpenOnly || v.isOpen;
    return matchCat && matchSearch && matchOpen;
  });

  return (
    <aside className="w-full lg:w-[380px] flex-shrink-0 flex flex-col h-full bg-stone-50 border-r border-stone-200">
      {/* Header */}
      <div className="px-4 pt-5 pb-3 bg-white border-b border-stone-100">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-forest-600">
            <i className="ri-map-2-line text-white text-sm" />
          </div>
          <div>
            <h1 className="text-base font-bold text-anthracite-900 leading-tight">Bursa Haritası</h1>
            <p className="text-xs text-stone-500">Tüm mekanları haritada keşfedin</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 text-sm" />
          <input
            type="text"
            placeholder="Mekan veya ilçe ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-2 text-sm bg-stone-100 rounded-lg border border-transparent focus:border-forest-400 focus:outline-none focus:bg-white transition-all placeholder-stone-400"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 cursor-pointer w-4 h-4 flex items-center justify-center"
            >
              <i className="ri-close-line text-sm" />
            </button>
          )}
        </div>

        {/* Open only toggle */}
        <button
          onClick={() => setShowOpenOnly(!showOpenOnly)}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
            showOpenOnly
              ? "bg-forest-600 text-white border-forest-600"
              : "bg-white text-stone-600 border-stone-200 hover:border-forest-400 hover:text-forest-600"
          }`}
        >
          <i className={`ri-door-open-line text-sm`} />
          Şu An Açık
        </button>
      </div>

      {/* Category Tabs */}
      <div className="px-3 pt-3 pb-2 bg-white border-b border-stone-100 overflow-x-auto">
        <div className="flex items-center gap-1.5 min-w-max">
          {CATEGORY_TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveCategory(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all cursor-pointer border ${
                activeCategory === tab.key
                  ? "bg-anthracite-900 text-white border-anthracite-900"
                  : "bg-white text-stone-600 border-stone-200 hover:border-stone-400 hover:text-anthracite-700"
              }`}
            >
              <i className={`${tab.icon} text-sm`} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Result Count */}
      <div className="px-4 py-2 flex items-center justify-between">
        <span className="text-xs text-stone-500">
          <span className="font-semibold text-anthracite-700">{filtered.length}</span> mekan gösteriliyor
        </span>
        {(activeCategory !== "all" || searchQuery || showOpenOnly) && (
          <button
            onClick={() => { setActiveCategory("all"); setSearchQuery(""); setShowOpenOnly(false); }}
            className="text-xs text-forest-600 hover:text-forest-700 cursor-pointer whitespace-nowrap"
          >
            Temizle
          </button>
        )}
      </div>

      {/* Venue List */}
      <div className="flex-1 overflow-y-auto px-3 pb-4 space-y-2">
        {filtered.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
              <i className="ri-map-pin-line text-3xl text-stone-300" />
            </div>
            <p className="text-sm text-stone-500">Sonuç bulunamadı</p>
            <p className="text-xs text-stone-400 mt-1">Filtrelerinizi değiştirin</p>
          </div>
        ) : (
          filtered.map((venue) => (
            <MapVenueCard
              key={venue.id}
              venue={venue}
              isSelected={selectedVenueId === venue.id}
              onClick={() => onSelectVenue(venue)}
            />
          ))
        )}
      </div>
    </aside>
  );
}
